/**
 * Leave — shared types and presentation helpers.
 * @file src/web/features/leave/leave.types.ts
 *
 * @llm-rule WHEN: Rendering a leave request anywhere (the leave page, the approval queue)
 * @llm-rule AVOID: Re-deriving the span wording per screen — use describeSpan
 */

import type { OrgRole } from '../../shared/domain';

export type LeaveType = 'CASUAL' | 'SICK' | 'ANNUAL' | 'UNPAID';
export type LeaveDayPart = 'FULL_DAY' | 'FIRST_HALF' | 'SECOND_HALF';
export type LeaveStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  /**
   * Approved leave the applicant has asked to call off.
   * The leave STILL STANDS in this state — they cannot clock in — until a Head
   * approves the cancellation.
   */
  | 'CANCEL_REQUESTED';

export interface LeaveApplicant {
  id: string;
  name: string | null;
  email?: string;
  orgRole: OrgRole;
  title: string | null;
  initials: string | null;
  organisationId?: string | null;
  teamId?: string | null;
  departmentId?: string | null;
}

export interface LeaveRequest {
  id: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  dayPart: LeaveDayPart;
  days: number;
  reason: string;
  status: LeaveStatus;
  decisionNote: string | null;
  /** Why they want approved leave called off, once they have asked. */
  cancelReason?: string | null;
  cancelRequestedAt?: string | null;
  decidedAt: string | null;
  createdAt: string;
  user?: LeaveApplicant;
  decidedBy?: { id: string; name: string | null; orgRole: OrgRole } | null;
}

export const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  CASUAL: 'Casual leave',
  SICK: 'Sick leave',
  ANNUAL: 'Annual leave',
  UNPAID: 'Unpaid leave',
};

export const DAY_PART_LABEL: Record<LeaveDayPart, string> = {
  FULL_DAY: 'Full day',
  FIRST_HALF: 'Half day — morning',
  SECOND_HALF: 'Half day — afternoon',
};

/** Background/foreground pairs for the status pill, from the theme tokens. */
export const LEAVE_STATUS_TONE: Record<LeaveStatus, { bg: string; fg: string }> = {
  PENDING: { bg: 'var(--state-warn-bg)', fg: 'var(--dot-offsite)' },
  APPROVED: { bg: 'var(--state-ok-bg)', fg: 'var(--dot-appreciation)' },
  REJECTED: { bg: 'var(--state-bad-bg)', fg: 'var(--dot-flag)' },
  CANCELLED: { bg: 'var(--paper-sunk)', fg: 'var(--ink-faint)' },
  CANCEL_REQUESTED: { bg: 'var(--state-warn-bg)', fg: 'var(--dot-offsite)' },
};

/** What the pill says. The enum name is not English. */
export const LEAVE_STATUS_LABEL: Record<LeaveStatus, string> = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
  CANCEL_REQUESTED: 'CALL-OFF ASKED',
};

/** Today as YYYY-MM-DD in the viewer's own timezone, for date inputs. */
export function todayISO(): string {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function pretty(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * One line describing when the leave falls.
 *   "12 Mar · half day (morning)"
 *   "12 Mar → 16 Mar · 5 days"
 */
export function describeSpan(row: Pick<LeaveRequest, 'startDate' | 'endDate' | 'dayPart' | 'days'>): string {
  const from = pretty(row.startDate);
  const to = pretty(row.endDate);

  if (from === to) {
    if (row.dayPart === 'FIRST_HALF') return `${from} · half day (morning)`;
    if (row.dayPart === 'SECOND_HALF') return `${from} · half day (afternoon)`;
    return `${from} · 1 day`;
  }

  return `${from} → ${to} · ${row.days} days`;
}
